/**
 * TabBar — Underline-style tab navigation
 *
 * Used for: screen-level tab switching (Overview / Low Stock / Movements)
 * From: InventoryReportsScreen tabs pattern
 */
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'

const TabBarFrame = styled(XStack, {
  name: 'TabBar',
  bg: '$backgroundCard',
  borderBottomWidth: 1,
  borderColor: '$borderColor',
})

const TabFrame = styled(YStack, {
  name: 'Tab',
  role: 'button',
  cursor: 'pointer',
  userSelect: 'none',
  f: 1,
  ai: 'center',
  py: '$sm',
  borderBottomWidth: 2,
  borderColor: 'transparent',
  animation: 'quick',
  animateOnly: ['opacity', 'transform'],
  pressStyle: { opacity: 0.7, scale: 0.97 },
  hoverStyle: { opacity: 0.85 },

  variants: {
    active: {
      true: {
        borderColor: '$colorPrimary',
      },
    },
  } as const,
})

const TabLabel = styled(TamaguiText, {
  name: 'TabLabel',
  fontFamily: '$body',
  fontSize: 14,
  fontWeight: '500',
  color: '$colorSecondary',

  variants: {
    active: {
      true: {
        color: '$colorPrimary',
        fontWeight: '600',
      },
    },
  } as const,
})

const TabBadge = styled(TamaguiText, {
  name: 'TabBadge',
  fontFamily: '$body',
  fontSize: 14,
  fontWeight: '600',
  color: '$colorError',
})

interface TabOption {
  value: string
  label: string
  badge?: string | number
}

type TabBarFrameProps = GetProps<typeof TabBarFrame>

interface TabBarProps extends TabBarFrameProps {
  options: TabOption[]
  value: string
  onValueChange: (value: string) => void
}

export const TabBar = TabBarFrame.styleable<TabBarProps>(
  ({ options, value, onValueChange, ...props }, ref) => {
    return (
      <TabBarFrame ref={ref} {...props}>
        {options.map((opt) => (
          <TabFrame
            key={opt.value}
            active={value === opt.value}
            onPress={() => onValueChange(opt.value)}
          >
            <XStack ai="center" gap={4}>
              <TabLabel active={value === opt.value}>{opt.label}</TabLabel>
              {opt.badge !== undefined && opt.badge !== 0 && (
                <TabBadge>({opt.badge})</TabBadge>
              )}
            </XStack>
          </TabFrame>
        ))}
      </TabBarFrame>
    )
  },
)
